import { useEffect, useMemo, useState, Fragment } from "react";
import axios from "@/api/api";
import { API_ROUTES } from "@/api/routes";
import { toast } from "react-toastify";
import { MdSearch } from "react-icons/md";
import { motion, AnimatePresence } from "framer-motion";
import { Listbox, Transition } from "@headlessui/react";
import { FaChevronDown } from "react-icons/fa";
import { useSession } from "@/context/SessionProvider";

export default function ConsultarAsignar() {
    const { user, loading: sessionLoading } = useSession();
    const perfilId = user?.perfil_id ?? null;
    const userId = user?.id ?? null;
    const esAdmin = Number(perfilId) === 1;

    const [datos, setDatos] = useState([]);
    const [modalVisible, setModalVisible] = useState(false);
    const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
    const [perfiles, setPerfiles] = useState([]);
    const [perfilSeleccionado, setPerfilSeleccionado] = useState("");
    const [busqueda, setBusqueda] = useState("");
    const [sortBy, setSortBy] = useState("empresa_nombre");
    const [sortAsc, setSortAsc] = useState(true);
    const [pagina, setPagina] = useState(1);
    const porPagina = 8;

    // Carga datos cuando la sesión esté lista
    useEffect(() => {
        if (sessionLoading) return;
        if (!user) return;
        fetchDatos();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionLoading, user]);

    const fetchDatos = async () => {
        try {
            const res = await axios.get(API_ROUTES.listadoEmpresasClientes, { withCredentials: true });
            let resultado = res.data || [];

            // Si perfil === 3 y la API devuelve usuario_id, filtramos por el usuario actual
            if (Number(perfilId) === 3 && resultado.length > 0 && "usuario_id" in resultado[0]) {
                resultado = resultado.filter((item) => item.usuario_id === Number(userId));
            }

            setDatos(resultado);
        } catch (err) {
            console.error("Error cargando datos", err);
            toast.error("Error al cargar empresas y clientes");
        }
    };

    const abrirModal = async (clienteId) => {
        setClienteSeleccionado(clienteId);
        setModalVisible(true);
        try {
            const res = await axios.get(API_ROUTES.listadoPerfilesTarifa, { withCredentials: true });
            setPerfiles(res.data || []);
        } catch (err) {
            console.error("Error cargando perfiles", err);
            toast.error("Error al obtener perfiles");
        }
    };

    const asignarPerfil = async () => {
        if (!perfilSeleccionado || !clienteSeleccionado) return;
        try {
            await axios.patch(
                API_ROUTES.listadoAsignarPerfil,
                { cliente_id: clienteSeleccionado, perfil_id: perfilSeleccionado.id },
                { withCredentials: true }
            );
            toast.success("Perfil asignado correctamente");
            setModalVisible(false);
            setPerfilSeleccionado("");
            fetchDatos();
        } catch (err) {
            console.error("Error asignando perfil", err);
            toast.error("Error al asignar perfil");
        }
    };

    const ordenarPor = (campo) => {
        if (campo === sortBy) setSortAsc(!sortAsc);
        else {
            setSortBy(campo);
            setSortAsc(true);
        }
    };

    const datosFiltrados = useMemo(
        () =>
            datos.filter((item) =>
                [item.empresa_nombre, item.cliente_nombre].some((val) =>
                    (val || "").toLowerCase().includes(busqueda.toLowerCase())
                )
            ),
        [datos, busqueda]
    );

    const datosOrdenados = useMemo(() => {
        const arr = [...datosFiltrados];
        arr.sort((a, b) => {
            const aVal = a[sortBy]?.toString().toLowerCase() ?? "";
            const bVal = b[sortBy]?.toString().toLowerCase() ?? "";
            if (aVal < bVal) return sortAsc ? -1 : 1;
            if (aVal > bVal) return sortAsc ? 1 : -1;
            return 0;
        });
        return arr;
    }, [datosFiltrados, sortBy, sortAsc]);

    const totalPaginas = Math.ceil(datosOrdenados.length / porPagina);
    const paginados = datosOrdenados.slice((pagina - 1) * porPagina, pagina * porPagina);

    const totalClientes = datos.length;
    const conPerfil = datos.filter((d) => d.perfil_id).length;
    const usuariosUnicos = new Set(datos.map((d) => d.usuario_nombre)).size;

    return (
        <div className="p-6">
            <h2 className="text-lg font-semibold mb-6">Consultar y Asignar Tarifas</h2>

            {/* Buscador */}
            <div className="relative">
                <input
                    type="text"
                    placeholder="Buscar empresa o cliente"
                    value={busqueda}
                    onChange={(e) => setBusqueda(e.target.value)}
                    className="pl-9 pr-3 py-2 text-sm border rounded-md shadow-sm w-64 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-base">
                    <MdSearch size={18} />
                </span>
            </div>

            {/* Tarjetas resumen */}
            {Number(perfilId) !== 4 && (
                <div className="grid md:grid-cols-3 sm:grid-cols-2 gap-4 mb-6">
                    <div className="bg-blue-50 p-4 rounded text-center shadow-sm">
                        <div className="text-xl font-bold">{totalClientes}</div>
                        <div className="text-sm text-gray-600">Total clientes</div>
                    </div>
                    <div className="bg-green-50 p-4 rounded text-center shadow-sm">
                        <div className="text-xl font-bold">{conPerfil}</div>
                        <div className="text-sm text-gray-600">Con perfil</div>
                    </div>
                    <div className="bg-yellow-50 p-4 rounded text-center shadow-sm">
                        <div className="text-xl font-bold">{usuariosUnicos}</div>
                        <div className="text-sm text-gray-600">Usuarios únicos</div>
                    </div>
                </div>
            )}

            {/* Tabla */}
            <div className="rounded-md overflow-hidden shadow-sm border border-gray-100">
                <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-left">
                        <tr>
                            {[
                                ["Empresa", "empresa_nombre"],
                                ["Cliente", "cliente_nombre"],
                                ["Usuario", "usuario_nombre"],
                                ["Perfil", null],
                            ].map(([label, key], idx) => (
                                <th
                                    key={idx}
                                    onClick={() => key && ordenarPor(key)}
                                    className={`px-4 py-2 font-medium ${key ? "cursor-pointer hover:text-blue-600" : ""}`}
                                >
                                    {label}
                                    {key === sortBy && <span className="ml-1">{sortAsc ? "▲" : "▼"}</span>}
                                </th>
                            ))}
                            {esAdmin && <th className="px-4 py-2 font-medium">Acción</th>}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {paginados.map((item, index) => (
                            <tr key={index} className="hover:bg-gray-50">
                                <td className="px-4 py-2">{item.empresa_nombre}</td>
                                <td className="px-4 py-2">{item.cliente_nombre}</td>
                                <td className="px-4 py-2">{item.usuario_nombre}</td>
                                <td className="px-4 py-2 text-xs leading-5">
                                    {item.perfil_id ? (
                                        <>
                                            {item.movil && <div className="text-blue-700 break-words">{item.movil}</div>}
                                            {item.fijo && <div className="text-green-700 break-words">{item.fijo}</div>}
                                            {item.buzon && <div className="text-pink-700 break-words">{item.buzon}</div>}
                                        </>
                                    ) : (
                                        <span className="text-gray-500 italic">No asignado</span>
                                    )}
                                </td>
                                {esAdmin && (
                                    <td className="px-4 py-2">
                                        <button
                                            onClick={() => abrirModal(item.cliente_id)}
                                            className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-3 py-1 rounded"
                                        >
                                            Asignar tarifa
                                        </button>
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Paginación */}
            <div className="flex justify-between items-center mt-4 text-sm">
                <span className="text-gray-500">
                    Mostrando {paginados.length} de {datosOrdenados.length}
                </span>
                <div className="flex items-center gap-1">
                    {Array.from({ length: totalPaginas }, (_, i) => i + 1).map((num) => (
                        <button
                            key={num}
                            onClick={() => setPagina(num)}
                            className={`px-3 py-1 rounded ${pagina === num ? "bg-blue-600 text-white" : "bg-gray-100 hover:bg-gray-200"
                                }`}
                        >
                            {num}
                        </button>
                    ))}
                </div>
            </div>

            {/* Modal */}
            <AnimatePresence>
                {modalVisible && esAdmin && (
                    <div className="fixed inset-0 bg-transparent backdrop-blur-sm flex items-center justify-center z-50">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.8 }}
                            transition={{ duration: 0.3, ease: "easeOut" }}
                            className="bg-white bg-opacity-80 backdrop-blur p-6 rounded-xl shadow-xl w-96"
                        >
                            <h3 className="font-semibold text-lg mb-4">Asignar perfil</h3>
                            <div className="mb-4">
                                <Listbox value={perfilSeleccionado} onChange={setPerfilSeleccionado}>
                                    <div className="relative">
                                        <Listbox.Button className="relative w-full cursor-pointer rounded border bg-white py-2 pl-3 pr-10 text-left text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
                                            <span>
                                                {perfilSeleccionado
                                                    ? `${perfilSeleccionado.id} - ${perfilSeleccionado.nombre || "Sin nombre"}`
                                                    : "Seleccione un perfil"}
                                            </span>
                                            <span className="pointer-events-none absolute inset-y-0 right-2 flex items-center pr-2">
                                                <FaChevronDown className="text-gray-400" />
                                            </span>
                                        </Listbox.Button>

                                        <Transition as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
                                            <Listbox.Options className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                                {perfiles.map((perfil) => (
                                                    <Listbox.Option
                                                        key={perfil.id}
                                                        value={perfil}
                                                        className={({ active }) =>
                                                            `cursor-pointer select-none px-4 py-2 ${active ? "bg-indigo-100" : ""}`
                                                        }
                                                    >
                                                        <div className="font-medium mb-1">Perfil {perfil.id}</div>
                                                        <div className="text-xs flex flex-col gap-1">
                                                            {perfil.movil && <div className="text-blue-700 break-words">{perfil.movil}</div>}
                                                            {perfil.fijo && <div className="text-green-700 break-words">{perfil.fijo}</div>}
                                                            {perfil.buzon && <div className="text-pink-700 break-words">{perfil.buzon}</div>}
                                                        </div>
                                                    </Listbox.Option>
                                                ))}
                                            </Listbox.Options>
                                        </Transition>
                                    </div>
                                </Listbox>
                            </div>
                            <div className="flex justify-end gap-2">
                                <button onClick={() => setModalVisible(false)} className="px-3 py-1 border rounded hover:bg-gray-100 transition">
                                    Cancelar
                                </button>
                                <button onClick={asignarPerfil} className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 transition">
                                    Asignar
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
}
